import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { statCardSurface } from "@/components/common/statCardSurface";

/**
 * Icon-box + big-number + label + subtext metric tile. Shared replacement for
 * the WorkingStatsBar stat tile and the CostFinancialsTab metric tile.
 */
export interface MetricCardProps {
  icon: LucideIcon;
  iconClassName?: string;
  value: ReactNode;
  label: string;
  subtext?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function MetricCard({
  icon: Icon,
  iconClassName,
  value,
  label,
  subtext,
  loading,
  className,
}: MetricCardProps) {
  return (
    <div
      className={cn(
        statCardSurface,
        "flex min-w-0 flex-1 flex-col justify-between p-2",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-(--color-border) bg-(--color-surface-content)">
          <Icon className={cn("h-6 w-6 text-(--color-text-primary)", iconClassName)} />
        </div>
        <span className="text-sm text-(--color-text-primary)/50">{label}</span>
      </div>
      <div className="px-3 pt-1">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-(--color-text-secondary)" />
        ) : (
          <>
            <span className="text-[3rem] font-medium leading-none text-(--color-text-primary)">
              {value ?? 0}
            </span>
            {subtext ? (
              <p className="mt-1 text-sm text-(--color-text-primary)/50">
                {subtext}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
